import styled, { IColors } from 'styled-components';
import { Logger } from '../../logging/LoggerService';
import { BodyText } from '../Typography/basicTypography';
import React from 'react';
import SpruceIcon from '../../images/spruce-icon-big.png';
import ZillowIcon from '../../images/zillow-icon-big.png';
import { useHasFeatureFlag } from '../../../selectors/useHasFeatureFlag';

export const UserIconStyled = styled(BodyText)<{ showLogoIcon?: boolean; iconText?: string }>`
  background-color: ${(props) => getIconBackgroundColor(props.theme.colors, props.showLogoIcon, props.iconText)};
  border: 1px solid ${(props) => getIconBorderColor(props.theme.colors, props.showLogoIcon, props.iconText)};
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const AdminLogo = styled.img`
  height: 100%;
  width: 100%;
`;

interface IUsesIconProps {
  firstName: string;
  lastName: string;
  showLogoIcon: boolean;
}

export const UserIcon: React.FC<IUsesIconProps> = ({ firstName, lastName, showLogoIcon }) => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const iconText = `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
  const adminIcon = isZillowClosingEnabled ? ZillowIcon : SpruceIcon;
  const altText = isZillowClosingEnabled ? 'zillow-icon' : 'spruce-icon';

  return (
    <UserIconStyled showLogoIcon={showLogoIcon} iconText={iconText}>
      {showLogoIcon ? <AdminLogo src={adminIcon} alt={altText} /> : iconText}
    </UserIconStyled>
  );
};

const getIconBackgroundColor = (colors: IColors, showLogoIcon?: boolean, iconText?: string): string => {
  const arrayOfColors = [
    colors.primaryOpacity20,
    colors.warningVariant,
    colors.yellowVariant,
    colors.secondaryVariant,
    colors.blueVariant,
    colors.errorBackground,
    colors.tertiaryVariant,
    colors.pinkVariant,
    colors.gray,
  ];
  return getColor(arrayOfColors, showLogoIcon, iconText);
};

const getIconBorderColor = (colors: IColors, showLogoIcon?: boolean, iconText?: string): string => {
  const arrayOfColors = [
    colors.primaryDark,
    colors.warningDark,
    colors.yellowDark,
    colors.secondaryDark,
    colors.blueDark,
    colors.errorDark,
    colors.purpleVariant,
    colors.pinkDark,
    colors.darkGray,
  ];
  return getColor(arrayOfColors, showLogoIcon, iconText);
};

const getColor = (arrayOfColors: string[], showLogoIcon?: boolean, iconText?: string): string => {
  const lastIndex = arrayOfColors.length - 1;
  if (!showLogoIcon) {
    return iconText ? arrayOfColors[getColorNumber(iconText, lastIndex)] : arrayOfColors[lastIndex];
  } else {
    return arrayOfColors[lastIndex];
  }
};

export const getColorNumber = (text: string, numberIfColors: number): number => {
  const hash = hashCode(text);
  if (numberIfColors > hash) {
    Logger.error(`Number of colors should be less then ${hash}`, {});
    return hash;
  } else if (numberIfColors === hash) {
    return hash;
  } else {
    return hash % numberIfColors;
  }
};

const hashCode = (s: string): number =>
  s.split('').reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
